import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Zap, Clock } from 'lucide-react';

/**
 * Sticky TOP CTA banner — always visible at top of homepage, just below the header.
 * Shows the Windows offer at 19€ + an optional flash sale countdown when active.
 */
const StickyCTABanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [flashSale, setFlashSale] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch flash sale state
  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '') return;
    let cancelled = false;

    const fetchFlashSale = async () => {
      try {
        const res = await fetch(`${API_URL}/api/flash-sale`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data && data.active) {
          // Only show countdown if the flash sale applies to optimization services (or to all)
          const services = Array.isArray(data.services) ? data.services : null;
          const optiKeywords = ['windows', 'pack', 'bios', 'optimisation', 'optimization', 'opti'];
          const appliesToOpti =
            !services ||
            services.length === 0 ||
            services.some((s) => optiKeywords.some((k) => String(s).toLowerCase().includes(k)));
          setFlashSale(appliesToOpti ? data : null);
        } else {
          setFlashSale(null);
        }
      } catch (_) {
        // silent
      }
    };

    fetchFlashSale();
    const poll = setInterval(fetchFlashSale, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [API_URL, location.pathname]);

  // Countdown timer
  useEffect(() => {
    if (!flashSale?.end_time) return;

    const tick = () => {
      const end = new Date(flashSale.end_time).getTime();
      const diff = end - Date.now();
      if (diff <= 0) {
        setFlashSale(null);
        return;
      }
      setTimeLeft({
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };

    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [flashSale]);

  if (location.pathname !== '/' && location.pathname !== '') return null;

  const pad = (n) => String(n).padStart(2, '0');
  const discount = flashSale?.discount_percent || 0;
  const discountedPrice = discount > 0 ? Math.round(19 * (1 - discount / 100)) : null;

  return (
    <div
      data-testid="sticky-cta-banner"
      className="fixed bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-40 px-4 w-full max-w-xl md:max-w-3xl"
    >
      <div className="relative flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3 md:py-3.5 rounded-2xl bg-[#0d0f1e]/95 border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.65)] backdrop-blur-md">

        {/* Left: icon */}
        <div className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center">
          <Zap className="w-5 h-5 md:w-6 md:h-6 text-indigo-400" />
        </div>

        <button
          onClick={() => navigate('/services')}
          data-testid="sticky-cta-btn"
          className="flex-1 flex items-center justify-between gap-3 text-left"
        >
          <div className="min-w-0">
            <p className="text-white text-sm md:text-base font-semibold leading-tight">
              Booster vos performances
            </p>

            {/* Price line — adapts when a flash sale is active */}
            {flashSale && discountedPrice !== null ? (
              <p className="text-xs md:text-sm mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="text-gray-500 line-through">19€</span>
                <span className="text-orange-300 font-semibold">À partir de {discountedPrice}€</span>
                <span
                  data-testid="sticky-cta-discount"
                  className="px-1.5 py-0.5 rounded-md bg-orange-500/20 border border-orange-500/40 text-orange-300 text-[10px] font-bold uppercase tracking-wide"
                >
                  -{discount}%
                </span>
              </p>
            ) : (
              <p className="text-gray-400 text-xs md:text-sm mt-0.5">
                À partir de 19€
              </p>
            )}

            {/* Countdown row — only when flash sale active */}
            {flashSale && (
              <p
                data-testid="sticky-cta-countdown"
                className="mt-1 flex items-center gap-1.5 text-orange-300/90 text-[10px] md:text-xs font-medium"
              >
                <Clock className="w-3 h-3 animate-pulse" />
                <span className="hidden sm:inline">Offre du jour — finit dans</span>
                <span className="sm:hidden">Finit dans</span>
                <span className="font-mono font-bold text-orange-200 tabular-nums">
                  {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m {pad(timeLeft.seconds)}s
                </span>
              </p>
            )}
          </div>

          <span className="flex-shrink-0 flex items-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm md:text-base font-semibold whitespace-nowrap transition-colors">
            Commander →
          </span>
        </button>
      </div>
    </div>
  );
};

export default StickyCTABanner;
